import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import './LegendeContinue.css';
import { linkify } from '../utils/linkify';

const CATEGORIES = [
  { key: '', label: 'Tout', color: '#8b1a2a' },
  { key: 'hommages', label: 'Hommages fans', color: '#8b1a2a' },
  { key: 'associations', label: 'Associations', color: '#b05070' },
  { key: 'solidaires', label: 'Actions solidaires', color: '#c47840' },
  { key: 'divers', label: 'Divers', color: '#9a8080' }
];
const CAT_COLOR = { hommages: '#8b1a2a', associations: '#b05070', solidaires: '#c47840', divers: '#9a8080' };
const CAT_LABEL = { hommages: 'Hommages fans', associations: 'Associations', solidaires: 'Actions solidaires', divers: 'Divers' };
const MONTHS_FR = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
const DAYS_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

function formatDate(d) {
  if (!d) return '';
  const date = new Date(d);
  return date.getDate() + ' ' + MONTHS_FR[date.getMonth()].slice(0,3) + '. ' + date.getFullYear();
}

export default function LegendeContinue() {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState('agenda');
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [activeEvent, setActiveEvent] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: 'hommages', eventDate: '', sourceUrl: '' });
  const [formFile, setFormFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef();
  const token = localStorage.getItem('mjtoken');

  const fetchItems = async (cat) => {
    const url = cat ? '/api/legend-news?category=' + cat : '/api/legend-news';
    const res = await fetch(url);
    const data = await res.json();
    if (Array.isArray(data)) {
      setItems(data);
      const upcoming = getNextEvent(data);
      setActiveEvent(upcoming);
    }
  };

  useEffect(() => { fetchItems(activeCategory); }, [activeCategory]);

  const getNextEvent = (data) => {
    const now = new Date();
    const future = data.filter(i => i.eventDate && new Date(i.eventDate) >= now)
      .sort((a,b) => new Date(a.eventDate) - new Date(b.eventDate));
    return future[0] || null;
  };

  const getEventsWithDate = () => items.filter(i => i.eventDate);

  const getMinMonth = () => {
    const evs = getEventsWithDate();
    if (!evs.length) return { month: new Date().getMonth(), year: new Date().getFullYear() };
    const min = evs.reduce((a,b) => new Date(a.eventDate) < new Date(b.eventDate) ? a : b);
    const d = new Date(min.eventDate);
    return { month: d.getMonth(), year: d.getFullYear() };
  };

  const getMaxMonth = () => {
    const evs = getEventsWithDate();
    if (!evs.length) return { month: new Date().getMonth(), year: new Date().getFullYear() };
    const max = evs.reduce((a,b) => new Date(a.eventDate) > new Date(b.eventDate) ? a : b);
    const d = new Date(max.eventDate);
    return { month: d.getMonth(), year: d.getFullYear() };
  };

  const canGoPrev = () => {
    const min = getMinMonth();
    return calYear > min.year || (calYear === min.year && calMonth > min.month);
  };

  const canGoNext = () => {
    const max = getMaxMonth();
    return calYear < max.year || (calYear === max.year && calMonth < max.month);
  };

  const goPrev = () => {
    if (!canGoPrev()) return;
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y-1); }
    else setCalMonth(m => m-1);
  };

  const goNext = () => {
    if (!canGoNext()) return;
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y+1); }
    else setCalMonth(m => m+1);
  };

  const getCalDays = () => {
    const first = new Date(calYear, calMonth, 1);
    const last = new Date(calYear, calMonth + 1, 0);
    const startDay = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startDay; i++) {
      days.push({ date: new Date(calYear, calMonth, -startDay + i + 1), other: true });
    }
    for (let i = 1; i <= last.getDate(); i++) days.push({ date: new Date(calYear, calMonth, i), other: false });
    while (days.length % 7 !== 0) {
      days.push({ date: new Date(calYear, calMonth + 1, days.length - last.getDate() - startDay + 1), other: true });
    }
    return days;
  };

  const getEventsForDay = (date) => items.filter(item => {
    if (!item.eventDate) return false;
    const d = new Date(item.eventDate);
    return d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
  });

  const hasEventsThisMonth = () => items.some(item => {
    if (!item.eventDate) return false;
    const d = new Date(item.eventDate);
    return d.getMonth() === calMonth && d.getFullYear() === calYear;
  });

  const today = new Date();
  const calDays = getCalDays();

  const handleSubmitForm = async () => {
    if (!form.title || !form.content) return;
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => fd.append(k, v));
    if (formFile) fd.append('image', formFile);
    await fetch('/api/legend-news', {
      method: 'POST',
      headers: token ? { Authorization: 'Bearer ' + token } : {},
      body: fd
    });
    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => {
      setShowForm(false); setSubmitted(false);
      setForm({ title: '', content: '', category: 'hommages', eventDate: '', sourceUrl: '' });
      setFormFile(null);
    }, 2500);
  };

  if (selected) {
    const color = CAT_COLOR[selected.category] || '#8b1a2a';
    return (
      <div className="lc-article-page">
        <div className="lc-article-inner">
          <button className="lc-back" onClick={() => setSelected(null)}>← Retour</button>
          <div className="lc-article-cat" style={{ color }}>
            <span className="lc-dot" style={{ background: color }}></span>
            {CAT_LABEL[selected.category]}
          </div>
          {selected.eventDate && <div className="lc-article-date">{formatDate(selected.eventDate)}</div>}
          <h1 className="lc-article-title">{selected.title}</h1>
          <div className="lc-article-split">
            {selected.imageUrl && <div className="lc-article-imgwrap"><img src={selected.imageUrl} alt={selected.title} className="lc-article-img" /></div>}
            <div className="lc-article-body">
              {selected.content.split('\n').map((p, i) => p.trim() ? <p key={i}>{p}</p> : null)}
            </div>
          </div>
          {selected.sourceUrl && <a href={selected.sourceUrl} target="_blank" rel="noreferrer" className="lc-source">Source →</a>}
        </div>
      </div>
    );
  }

  return (
    <div className="lc-page">
      <div className="lc-eyebrow">Communaute</div>
      <h1 className="lc-title">La legende continue, <em>son heritage vivant</em></h1>
      <p className="lc-intro">Michael Jackson n'est pas seulement une memoire — il est une force qui continue d'inspirer des milliers d'actions a travers le monde.</p>
      <div className="lc-maintabs">
        <button className={'lc-maintab' + (mainTab === 'agenda' ? ' active' : '')} onClick={() => setMainTab('agenda')}>Agenda</button>
        <button className={'lc-maintab' + (mainTab === 'actus' ? ' active' : '')} onClick={() => setMainTab('actus')}>Actualites</button>
      </div>
      {mainTab === 'actus' ? <LegendeActusGrid user={user} /> : (
      <>

      <div className="lc-toolbar">
        <div className="lc-tabs">
          {CATEGORIES.map(c => (
            <button key={c.key} className={'lc-tab' + (activeCategory === c.key ? ' active' : '')}
              style={activeCategory === c.key ? { background: c.color, borderColor: c.color } : {}}
              onClick={() => setActiveCategory(c.key)}>{c.label}</button>
          ))}
        </div>
        {user && <button className="lc-btn-propose" onClick={() => setShowForm(true)}>✦ Proposer une actu</button>}
      </div>

      <div className="lc-cal-wrap">
        <div className="lc-cal">
          <div className="lc-cal-header">
            <div className="lc-cal-month">{MONTHS_FR[calMonth]} {calYear}</div>
            <div className="lc-cal-nav">
              <button onClick={goPrev} disabled={!canGoPrev()} style={{ opacity: canGoPrev() ? 1 : 0.3 }}>‹</button>
              <button onClick={goNext} disabled={!canGoNext()} style={{ opacity: canGoNext() ? 1 : 0.3 }}>›</button>
            </div>
          </div>
          <div className="lc-cal-grid">
            {DAYS_FR.map(d => <div key={d} className="lc-cal-dayname">{d}</div>)}
            {calDays.map((day, i) => {
              const events = getEventsForDay(day.date);
              const isToday = !day.other && day.date.toDateString() === today.toDateString();
              return (
                <div key={i} className={'lc-cal-day' + (day.other ? ' other' : '')}>
                  <div className={'lc-cal-num' + (isToday ? ' today' : '')}>{day.date.getDate()}</div>
                  {events.slice(0,2).map(ev => (
                    <div key={ev._id} className="lc-cal-event"
                      style={{ background: CAT_COLOR[ev.category] + '20', borderLeft: '2px solid ' + CAT_COLOR[ev.category], color: CAT_COLOR[ev.category] }}
                      onClick={() => setActiveEvent(ev)}>
                      {ev.title.slice(0, 18)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {!hasEventsThisMonth() && (
            <div className="lc-cal-empty">Aucun evenement prevu ce mois-ci</div>
          )}
        </div>

        <div className="lc-event-card">
          {activeEvent ? (
            <>
              {activeEvent.imageUrl
                ? <img src={activeEvent.imageUrl} alt={activeEvent.title} className="lc-event-img" />
                : <div className="lc-event-img-placeholder"></div>
              }
              <div className="lc-event-body">
                <div className="lc-event-badge">
                  {activeEvent.eventDate && new Date(activeEvent.eventDate) >= today ? '⟶ Prochain evenement' : '◉ Evenement selectionne'}
                </div>
                <div className="lc-event-cat" style={{ color: CAT_COLOR[activeEvent.category] }}>
                  <span className="lc-dot" style={{ background: CAT_COLOR[activeEvent.category] }}></span>
                  {CAT_LABEL[activeEvent.category]}
                </div>
                <div className="lc-event-title">{activeEvent.title}</div>
                {activeEvent.eventDate && <div className="lc-event-date">{formatDate(activeEvent.eventDate)}</div>}
                <div className="lc-event-excerpt">{activeEvent.content.slice(0, 150)}{activeEvent.content.length > 150 ? '...' : ''}</div>
                <div className="lc-event-link" onClick={() => setSelected(activeEvent)}>Lire l'article →</div>
              </div>
            </>
          ) : (
            <div className="lc-event-none">Aucun evenement a venir.</div>
          )}
        </div>
      </div>

      <div className="lc-section-title">Toutes les actus</div>
      {items.length === 0 && <p className="lc-empty">Aucune actu pour l'instant.</p>}
      <div className="lc-masonry">
        {items.map(item => {
          const color = CAT_COLOR[item.category] || '#8b1a2a';
          const excerpt = item.content.length > 150 ? item.content.slice(0, 150) + '...' : item.content;
          return (
            <div key={item._id} className={'lc-card' + (item.pinned ? ' pinned' : '')} onClick={() => setSelected(item)}
              style={item.pinned ? { borderColor: color } : {}}>
              {item.pinned && <div className="lc-pin">📌</div>}
              {item.imageUrl ? <img src={item.imageUrl} alt={item.title} className="lc-card-img" /> : <div className="lc-card-img-placeholder"></div>}
              <div className="lc-card-body">
                <div className="lc-cat" style={{ color }}>
                  <span className="lc-dot" style={{ background: color }}></span>
                  {CAT_LABEL[item.category]}
                </div>
                <div className="lc-card-title">{item.title}</div>
                <div className="lc-card-excerpt">{excerpt}</div>
                <div className="lc-card-footer">
                  <span className="lc-read" style={{ color }}>Lire →</span>
                  {item.eventDate && <span className="lc-date">{formatDate(item.eventDate)}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="lc-overlay" onClick={e => { if (e.target.className === 'lc-overlay') setShowForm(false); }}>
          <div className="lc-popup">
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: 17, color: '#8b1a2a' }}>Proposition envoyee !</div>
                <p style={{ fontSize: 13, color: '#9a8080', marginTop: 8 }}>Elle sera examinee par l'admin avant publication.</p>
              </div>
            ) : (
              <>
                <div className="lc-popup-title">Proposer une actu</div>
                <p className="lc-popup-sub">Votre proposition sera examinee par l'admin avant publication.</p>
                <div className="lc-field"><label>Titre *</label><input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Ex : Flashmob a Lyon" /></div>
                <div className="lc-field"><label>Categorie *</label>
                  <select value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))}>
                    <option value="hommages">Hommages fans</option>
                    <option value="associations">Associations</option>
                    <option value="solidaires">Actions solidaires</option>
                    <option value="divers">Divers</option>
                  </select>
                </div>
                <div className="lc-field"><label>Date de l'evenement</label><input type="date" value={form.eventDate} onChange={e => setForm(p => ({...p, eventDate: e.target.value}))} /></div>
                <div className="lc-field"><label>Description *</label><textarea rows={4} value={form.content} onChange={e => setForm(p => ({...p, content: e.target.value}))} placeholder="Decrivez l'evenement..."></textarea></div>
                <div className="lc-field"><label>Lien source (optionnel)</label><input value={form.sourceUrl} onChange={e => setForm(p => ({...p, sourceUrl: e.target.value}))} placeholder="https://..." /></div>
                <div className="lc-field"><label>Photo (optionnel)</label><input ref={fileRef} type="file" accept="image/*" onChange={e => setFormFile(e.target.files[0])} /></div>
                <div className="lc-popup-btns">
                  <button className="lc-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                  <button className="lc-btn-submit" onClick={handleSubmitForm} disabled={submitting || !form.title || !form.content}>
                    {submitting ? 'Envoi...' : 'Envoyer ma proposition'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function LegendeActusGrid({ user }) {
  const CAT_COLOR = { hommages: '#8b1a2a', associations: '#b05070', solidaires: '#c47840', divers: '#9a8080' };
  const CAT_LABEL = { hommages: 'Hommages fans', associations: 'Associations', solidaires: 'Actions solidaires', divers: 'Divers' };
  const [actus, setActus] = useState([]);
  const [open, setOpen] = useState(null);
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('mjtoken') : null;
  const myId = user?._id || user?.id;

  const fetchActus = async () => {
    try { const res = await fetch('/api/legende-actus'); const data = await res.json(); if (Array.isArray(data)) setActus(data); } catch {}
  };
  useEffect(() => { fetchActus(); }, []);

  if (open) {
    const liked = !!myId && (open.likes || []).some(id => String(id) === String(myId));
    return <LegendeActuArticle actu={open} liked={liked} token={token} myId={myId}
      onBack={() => { setOpen(null); fetchActus(); }} CAT_COLOR={CAT_COLOR} CAT_LABEL={CAT_LABEL} />;
  }

  return (
    <div className="lc-actus-grid">
      {actus.length === 0 && <div className="lc-actus-empty">Aucune actualite pour l'instant.</div>}
      {actus.map(a => (
        <div key={a._id} className="lc-actu-card" onClick={() => setOpen(a)}>
          <div className="lc-actu-imgwrap">
            {a.imageUrl ? <img src={a.imageUrl} alt={a.title} className="lc-actu-img" /> : <div className="lc-actu-img-ph"></div>}
            <span className="lc-actu-badge" style={{ background: CAT_COLOR[a.category] }}>{CAT_LABEL[a.category] || a.category}</span>
          </div>
          <div className="lc-actu-body">
            <div className="lc-actu-title">{a.title}</div>
            <div className="lc-actu-meta">
              <span>{'\u2665'} {(a.likes || []).length}</span>
              <span>{'\uD83D\uDCAC'} {(a.comments || []).length}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LegendeActuArticle({ actu, liked: likedInit, token, myId, onBack, CAT_COLOR, CAT_LABEL }) {
  const [likes, setLikes] = useState((actu.likes || []).length);
  const [liked, setLiked] = useState(likedInit);
  const [comments, setComments] = useState(actu.comments || []);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const toggleLike = async () => {
    if (!token) return;
    const res = await fetch('/api/legende-actus/' + actu._id + '/like', { method: 'PATCH', headers: { Authorization: 'Bearer ' + token } });
    if (res.ok) { const d = await res.json(); setLikes(d.likes); setLiked(d.liked); }
  };
  const sendComment = async () => {
    if (!text.trim() || !token) return;
    setSending(true);
    const res = await fetch('/api/legende-actus/' + actu._id + '/comment', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ text })
    });
    if (res.ok) { const c = await res.json(); setComments(prev => [...prev, c]); setText(''); }
    setSending(false);
  };

  const color = CAT_COLOR[actu.category];
  return (
    <div className="lc-article-page">
      <div className="lc-article-inner">
        <button className="lc-back" onClick={onBack}>← Retour</button>
        <div className="lc-article-cat" style={{ color }}>
          <span className="lc-dot" style={{ background: color }}></span>
          {CAT_LABEL[actu.category] || actu.category}
        </div>
        <h1 className="lc-article-title">{actu.title}</h1>
        <div className="lc-article-split">
          {actu.imageUrl && <div className="lc-article-imgwrap"><img src={actu.imageUrl} alt={actu.title} className="lc-article-img" /></div>}
          <div className="lc-article-body">
            {actu.content.split('\n').map((p, i) => p.trim() ? <p key={i}>{linkify(p)}</p> : null)}
          </div>
        </div>

        <div className="lc-actu-actions">
          <button className="lc-actu-like" onClick={toggleLike} style={{ color: liked ? '#8b1a2a' : '#9a8080' }}>
            {liked ? '\u2665' : '\u2661'} {likes} j'aime
          </button>
          <span className="lc-actu-ccount">{comments.length} commentaire{comments.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="lc-actu-comments">
          {comments.map((c, i) => (
            <div key={i} className="lc-actu-comment">
              <div className="lc-actu-comment-author">{c.authorName}</div>
              <div className="lc-actu-comment-text">{linkify(c.text)}</div>
            </div>
          ))}
          {token ? (
            <div className="lc-actu-cform">
              <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendComment()} placeholder="Ajouter un commentaire..." className="lc-actu-cinput" />
              <button onClick={sendComment} disabled={sending || !text.trim()} className="lc-actu-csend">{'\u2192'}</button>
            </div>
          ) : <div className="lc-actu-clogin">Connectez-vous pour aimer et commenter.</div>}
        </div>
      </div>
    </div>
  );
}
